
import React from 'react';
import { ChevronRight, Shield, Zap, Lock, Cpu, Sparkles } from 'lucide-react';

interface HeroProps {
  onExplore?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onExplore }) => {
  return (
    <section id="home" className="relative min-h-[110vh] flex items-center pt-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16 items-center relative z-10">
        
        <div className="lg:col-span-7 space-y-12">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-brand-500/10 border border-brand-500/20 backdrop-blur-3xl group glint">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
            </span>
            <span className="text-[10px] font-black text-brand-300 uppercase tracking-[0.4em] font-mono">
              ASTRA PROTOCOL • V4.0 LIVE
            </span>
          </div>
          
          <div className="space-y-8">
            <h1 className="text-7xl md:text-[9.5rem] font-black leading-[0.8] tracking-[-0.06em] text-white">
              Securing <br />
              <span className="premium-gradient bg-clip-text">Stellar</span> <br />
              Intelligence.
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 max-w-2xl leading-relaxed font-light tracking-tight opacity-80">
              Elite-tier cybersecurity and autonomous AI defense systems architected for the next century of digital progress.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-8 pt-4">
            <button 
              onClick={onExplore}
              className="px-14 py-6 rounded-full bg-brand-500 text-black font-black text-xs tracking-[0.3em] uppercase transition-all hover:bg-brand-400 hover:scale-[1.05] shadow-[0_0_60px_rgba(0,209,255,0.4)] active:scale-95 flex items-center gap-4 group"
            >
              INITIATE DEFENSE
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
            </button>
            <a href="#about" className="px-14 py-6 rounded-full glass-premium text-white font-black text-xs tracking-[0.3em] uppercase transition-all hover:bg-white/10 border border-white/15 flex items-center gap-4 group">
              THE VISION
              <Sparkles className="w-4 h-4 text-brand-400 group-hover:rotate-12 transition-transform" />
            </a>
          </div>

          <div className="grid grid-cols-3 gap-16 pt-20 border-t border-white/5">
            {[
              { label: 'Network Integrity', val: 'Quantum' },
              { label: 'Global Latency', val: 'Sub-Zero' },
              { label: 'Security Class', val: 'Astra-9' }
            ].map((stat, i) => (
              <div key={i} className="space-y-3 relative group cursor-help">
                <div className="text-3xl font-black text-white tracking-tighter group-hover:text-brand-500 transition-colors">{stat.val}</div>
                <div className="text-[9px] text-slate-500 uppercase font-mono tracking-[0.4em] font-black group-hover:text-slate-300 transition-colors">{stat.label}</div>
                <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-brand-500/30 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <div className="relative flex items-center justify-center">
            <div className="w-[550px] h-[550px] rounded-full relative flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-[0.5px] border-white/5 animate-[spin_50s_linear_infinite]" />
              <div className="absolute inset-12 rounded-full border-[0.5px] border-brand-500/20 animate-[spin_70s_linear_infinite_reverse]" />
              <div className="absolute inset-24 rounded-full border-[0.5px] border-white/10 animate-[spin_30s_linear_infinite]" />
              
              <div className="relative z-20 w-80 h-80 glass-premium rounded-[5rem] border border-brand-500/30 shadow-[0_0_150px_rgba(0,209,255,0.25)] flex flex-col items-center justify-center p-12 text-center group overflow-hidden glint">
                <div className="hologram-line" />
                <div className="absolute inset-0 bg-brand-500/5 group-hover:bg-brand-500/10 transition-colors" />
                
                <div className="w-28 h-28 rounded-[2.5rem] bg-brand-500 flex items-center justify-center shadow-[0_0_80px_rgba(0,209,255,0.5)] mb-10 relative">
                   <div className="absolute inset-0 stellar-core animate-pulse" />
                   <Cpu className="w-14 h-14 text-black relative z-10" />
                </div>
                
                <div className="font-black text-xs uppercase tracking-[0.5em] text-brand-500 mb-3">CORE ASTRA</div>
                <div className="text-[10px] text-slate-500 font-mono tracking-tighter uppercase font-black opacity-60">Authentication Layer 4.0.1</div>
              </div>

              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-16 glass-premium rounded-3xl border-brand-500/40 flex items-center justify-center glint">
                <Shield className="w-7 h-7 text-brand-500" />
              </div>
              <div className="absolute bottom-1/4 -right-12 w-16 h-16 glass-premium rounded-3xl border-purple-500/40 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-purple-500" />
              </div>
              <div className="absolute top-1/4 -left-12 w-14 h-14 glass-premium rounded-3xl border-brand-400/40 flex items-center justify-center">
                <Lock className="w-6 h-6 text-brand-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-[#02040a] via-[#02040a]/90 to-transparent pointer-events-none z-10" />
    </section>
  );
};

export default Hero;
